import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { Menu, X, LogOut, User, Sparkles, ChevronDown, Settings, LayoutDashboard } from 'lucide-react';
import mockData from '../mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  const handleDropdownItemClick = async (action) => {
    setProfileDropdownOpen(false);
    
    if (action === 'dashboard') {
      // Check if user is verified before allowing dashboard access
      if (user && !user.is_verified) {
        navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
      } else {
        navigate('/dashboard');
      }
    } else if (action === 'settings') {
      // Check if user is verified before allowing settings access
      if (user && !user.is_verified) {
        navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
      } else {
        navigate('/settings');
      }
    } else if (action === 'logout') {
      handleLogout();
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-gray-950/95 backdrop-blur-xl border-b border-violet-500/30 shadow-lg shadow-violet-500/10' 
        : 'bg-gray-950/90 backdrop-blur-md border-b border-violet-500/20'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              {/* Chatbot Logo Image - Made Larger, No Border/Glow */}
              <div className="relative w-14 h-14 rounded-xl overflow-hidden transition-all duration-300">
                <img 
                  src="/logo.jpeg" 
                  alt="BrixBot Logo" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to gradient background if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentElement.style.background = 'linear-gradient(135deg, #8b5cf6, #a855f7)';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-white font-bold text-xl">B</div>';
                  }}
                />
              </div>
            </div>
            
            {/* BrixBot Gradient Text */}
            <div className="relative">
              <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 group-hover:from-violet-300 group-hover:via-purple-300 group-hover:to-fuchsia-300 transition-all duration-300">
                BrixBot
              </span>
              <div className="absolute -inset-1 bg-gradient-to-r from-violet-400 to-purple-400 blur opacity-20 group-hover:opacity-30 transition-all duration-300 rounded-lg"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {mockData.navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  isActiveRoute(item.href)
                    ? 'text-violet-400 drop-shadow-sm'
                    : 'text-gray-300 hover:text-violet-300'
                }`}
              >
                {item.name}
                {isActiveRoute(item.href) && (
                  <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"></div>
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                <Button
                  variant="ghost"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="text-gray-300 hover:text-violet-300 hover:bg-violet-500/10 border-violet-500/20 hover:border-violet-400/40 transition-all duration-300 flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <span>{user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </Button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-lg border border-violet-500/20 py-2 z-50">
                    <button
                      onClick={() => handleDropdownItemClick('dashboard')}
                      className="w-full px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-violet-500/10 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('settings')}
                      className="w-full px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-violet-500/10 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </button>
                    <hr className="border-gray-700 my-2" />
                    <button
                      onClick={() => handleDropdownItemClick('logout')}
                      className="w-full px-4 py-2 text-left text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-violet-300 hover:bg-violet-500/10 border-violet-500/20 hover:border-violet-400/40 transition-all duration-300"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-violet-300 hover:bg-violet-500/10 transition-all duration-300"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-950/98 backdrop-blur-xl border-t border-violet-500/20 shadow-lg shadow-violet-500/10">
          <div className="px-4 py-6 space-y-4">
            {isAuthenticated && user?.role === 'employee' ? (
              <>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleDropdownItemClick('dashboard');
                  }}
                  className={`block text-base font-medium transition-all duration-300 ${
                    isActiveRoute('/dashboard') ? 'text-violet-400' : 'text-gray-300 hover:text-violet-300'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleDropdownItemClick('settings');
                  }}
                  className={`block text-base font-medium transition-all duration-300 ${
                    isActiveRoute('/settings') ? 'text-violet-400' : 'text-gray-300 hover:text-violet-300'
                  }`}
                >
                  Settings
                </button>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleDropdownItemClick('logout');
                  }}
                  className="block text-base font-medium text-red-400 hover:text-red-300 transition-all duration-300"
                >
                  Logout
                </button>
              </>
            ) : (
              mockData.navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block text-base font-medium transition-all duration-300 ${
                    isActiveRoute(item.href)
                      ? 'text-violet-400'
                      : 'text-gray-300 hover:text-violet-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))
            )}
            
            <div className="pt-4 space-y-2">
              {isAuthenticated ? (
                <>
                  <div
                    onClick={() => {
                      setMobileMenuOpen(false);
                      // Check if user is verified before allowing dashboard access
                      if (user && !user.is_verified) {
                        navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
                      } else {
                        navigate('/dashboard');
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-gray-300 hover:text-violet-300 hover:bg-violet-500/10"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </Button>
                  </div>
                  <div className="px-4 py-2 text-gray-400 text-sm font-medium">
                    {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email}
                  </div>
                  <div
                    onClick={() => {
                      setMobileMenuOpen(false);
                      // Check if user is verified before allowing settings access
                      if (user && !user.is_verified) {
                        navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
                      } else {
                        navigate('/settings');
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-gray-300 hover:text-violet-300 hover:bg-violet-500/10"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start text-gray-300 hover:text-violet-300 hover:bg-violet-500/10"
                    >
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;